import type { TelemetryEventDataMap } from '@src/telemetry/telemetry-events.types'
import { trackEvent } from '@src/telemetry/track'
import type { SvdNativeCardVariant } from '@src/types/svd-product-card.types'

/**
 * The anchor-generic (VDA) analytics keys shared by every SVD card event (click, impression,
 * variant-select, wishlist add/remove). SVD generalizes the swatch dimension beyond colour, so the
 * legacy hardcoded `color` key alone is ambiguous for a non-colour anchor (`color:'14K'` could be a
 * metalCarat). These keys let each event self-describe its anchor:
 *
 *  - `display_config` — the swatch dimension: `{ name, value }`, e.g. `{ name:'color', value:'Brown' }`
 *    or `{ name:'metalCarat', value:'14K' }` (from `displayConfig.name` / `displayConfig.value`).
 *  - `selected_attributes` — the SKU-scoping attribute set, forwarded verbatim from the wire
 *    (no casing normalization — client policy is to forward the backend value as-is).
 *
 * Purely additive to the existing `color` keys, so downstream consumers are unaffected. Both keys are
 * omitted for a no-VDA variant (null `displayConfig` / `selectedAttributes`). This is the single seam
 * every event spreads — a new card event stays VDA-generic by adding one call, no per-event colour
 * logic.
 */
export interface SvdAnchorAnalyticsFields {
  display_config?: { name: string; value: string }
  selected_attributes?: Record<string, string>
}

export function getSvdAnchorAnalyticsFields(variant?: SvdNativeCardVariant): SvdAnchorAnalyticsFields {
  const displayConfig = variant?.displayConfig
  return {
    ...(displayConfig ? { display_config: { name: displayConfig.name, value: displayConfig.value } } : {}),
    ...(variant?.selectedAttributes ? { selected_attributes: variant.selectedAttributes } : {}),
  }
}

/**
 * Canonical emitter for every SVD card analytics event fired via `trackEvent` (click, variant-select,
 * wishlist add/remove). Injects the anchor keys into the payload's `context` — the one location every
 * InternalAnalytics card handler forwards (`...properties.context`) — so `display_config` +
 * `selected_attributes` reach the wire regardless of each handler's top-level whitelist. Route ALL SVD
 * card events through this and a new event stays VDA-generic for free: pass the selected variant, done —
 * no per-event or per-IA-handler wiring, and no need to remember where the keys go.
 *
 * The batched impression event (`BULK_PRODUCT_CARD_VIEW`) is the one card event NOT emitted via
 * `trackEvent` — it pushes to the batched-analytics stream and forwards the same keys through
 * `getProductDataForBatchedAnalytics` instead. Anything using `trackEvent` should use this.
 */
export function trackSvdCardEvent<T extends keyof TelemetryEventDataMap>(
  event: T,
  payload: { context?: Record<string, unknown> } & Record<string, unknown>,
  variant?: SvdNativeCardVariant,
) {
  trackEvent(event, {
    ...payload,
    context: { ...(payload.context ?? {}), ...getSvdAnchorAnalyticsFields(variant) },
  })
}
